import React, { useEffect, useState } from 'react'
import clsx from 'clsx'
import { navigate } from 'gatsby'
import Layout from 'components/layouts/tow-column'
import {
  Button,
  Card,
  Divider,
  ErrorAlert,
  Form,
  FormProps,
  Title,
  Select,
} from 'components/common'
import {
  AddressAndDetailsForm,
  DiscountForm,
  NameAndEmailForm,
  NoteForm,
} from '../forms'
import {
  CountryCode,
  CustomerCreateInput,
  CustomerType,
  CustomerUpdateInput,
  PrefectureCode,
  useGeneralSettingQuery,
} from 'lib/graphql/storeadmin'
import { Modal } from 'antd'

interface Discount {
  id: string
  title: string
  description?: string
  canOverlap?: boolean
}
interface DiscountEdge {
  node?: Discount
}

export type EdgeArray = DiscountEdge[]

type FormValues = {
  firstName: string
  lastName: string
  phone: string
  email: string
  password?: string
  corporationName: string
  corporationNameKana: string
  note: string
  address1: string
  address2: string
  prefectureCode: string
  zip: string
  city: string
  discount: number
  limit: number
  bookableFlag: boolean
  subArenaBookableFlag: boolean
  prefecturalGroupFlag: boolean
  storeWideDiscount?: string
  discountWithTargetProducts?: string[]
  appliedDiscount?: EdgeArray
  type?: string
}

type PickedFormProps = Pick<FormProps, 'onFinishFailed'>

export interface EditorProps extends PickedFormProps {
  // 新規作成かどうか
  isNew?: boolean
  title: string
  defaultValues?: Partial<FormValues>
  type: 'personal' | 'corporate' | 'unregistered'
  loading?: boolean
  // リクエスト送信中かどうか
  requesting?: boolean
  onFinish: (
    allowDuplicate: boolean,
    values: CustomerUpdateInput | CustomerCreateInput
  ) => void
  showAlert?: boolean
  onCloseAlert?: () => void
  alertDescription?: string[]
}

const defaultExemptionsNumber = 10

const onBack = () => navigate('../')

const onCancel = async () => {
  // TODO: 入力中のデータが消えるがいいかを確認してから遷移
  const cancelAlert = await new Promise<boolean>((resolve) => {
    Modal.confirm({
      title: '編集のキャンセル',
      content: '編集中のデータは失われます。よろしいですか？',
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
      okText: 'はい',
      cancelText: 'いいえ',
    })
  })
  if (cancelAlert) navigate('../')
}

export const Editor = ({
  isNew,
  title,
  defaultValues,
  type,
  loading,
  requesting,
  onFinish,
  onFinishFailed,
  showAlert,
  onCloseAlert,
  alertDescription,
}: EditorProps): JSX.Element => {
  const { data: generalSettingData, loading: generalSettingLoading } =
    useGeneralSettingQuery()
  const [resetRequired, setResetRequired] = useState(true)
  const [isBookableFlag, setIsBookableFlag] = useState<boolean>(
    defaultValues?.bookableFlag
  )
  const [isType, setIsType] = useState(type)
  const [isSubArenaFlag, setIsSubArenaFlag] = useState<boolean>(
    defaultValues?.subArenaBookableFlag ?? false
  )
  const [form] = Form.useForm<FormValues>()
  useEffect(() => {
    if (resetRequired) {
      form.resetFields()
      setResetRequired(false)
    }
  }, [defaultValues, form, resetRequired])

  const {
    firstName,
    lastName,
    phone,
    email,
    password,
    corporationName,
    corporationNameKana,
    note,
    address1,
    address2,
    prefectureCode,
    zip,
    city,
    appliedDiscount,
    bookableFlag,
  } = defaultValues || {}

  const customOnFinish = async (values: FormValues): Promise<void> => {
    // 保存の前にポップを表示
    const shouldContinue = await new Promise<boolean>((resolve) => {
      Modal.confirm({
        title: '顧客情報編集',
        content: '入力された内容で保存しますか？',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
        okText: 'はい',
        cancelText: 'いいえ',
      })
    })
    if (!shouldContinue) return

    const input = {
      firstName: values.firstName,
      lastName: values.lastName,
      phone: values.phone,
      email: values.email,
      password: values.password,
      type:
        type === 'unregistered'
          ? (values.type as CustomerType)
          : (type.toUpperCase() as CustomerType),
      corporationName: values.corporationName,
      corporationNameKana: values.corporationNameKana,
      note: values.note ?? undefined,
      address: {
        address1: values.address1,
        address2: values.address2,
        countryCode: CountryCode.Jpn,
        prefectureCode: values.prefectureCode as PrefectureCode,
        zip: values.zip,
        city: values.city,
      },
      userMetaData: {
        defaultLimit: defaultExemptionsNumber,
        // 値が無ければエラーになるので最低でも0を入れる
        discount: values.discount ? values.discount : 0,
        limit: values.limit ? values.limit : 0,
        grantedAt: new Date(),
      },
      bookableFlag: values.bookableFlag,
      subArenaBookableFlag: values.subArenaBookableFlag,
      // サブアリーナ予約可能フラグがfalseならfalseに設定する
      prefecturalGroupFlag: !isSubArenaFlag
        ? false
        : values.prefecturalGroupFlag,
      storeWideDiscount:
        values.storeWideDiscount || values.storeWideDiscount === ''
          ? values.storeWideDiscount
          : null,
      discountWithTargetProducts:
        values.discountWithTargetProducts &&
        values.discountWithTargetProducts.length > 0
          ? values.discountWithTargetProducts
          : null,
    }
    onFinish(false, input)
  }

  return (
    <>
      <Form
        form={form}
        component="form"
        requiredMark="optional"
        onFinish={customOnFinish}
        onFinishFailed={onFinishFailed}
      >
        <Layout title={title} loading={loading} onBack={onBack}>
          {/* 顧客タイプが「未登録」の場合、顧客タイプのセレクトボックスを表示 */}
          {type === 'unregistered' && (
            <>
              <Layout.Section size="small">
                <Title level={5}>お客様の顧客タイプ</Title>
              </Layout.Section>
              <Layout.Section size="large">
                <Card shadow loading={loading}>
                  <Form.Item
                    name="type"
                    rules={[
                      {
                        required: true,
                        message: '顧客タイプは必ず選択してください',
                      },
                    ]}
                  >
                    <Select
                      className="w-1/2"
                      placeholder="未登録"
                      onChange={(value) => setIsType(value.toLowerCase())}
                    >
                      <Select.Option value={CustomerType.Personal}>
                        個人
                      </Select.Option>
                      <Select.Option value={CustomerType.Corporate}>
                        法人・団体
                      </Select.Option>
                    </Select>
                  </Form.Item>
                </Card>
              </Layout.Section>
              <Divider space="large" />
            </>
          )}

          {/* 顧客タイプが「未登録」かつ、顧客タイプの選択がされていない場合、フォームを非表示 */}
          {type === 'unregistered' && isType === 'unregistered' ? null : (
            <>
              <Layout.Section size="small">
                <Title level={5}>お客様の概要</Title>
              </Layout.Section>
              <Layout.Section size="large">
                {showAlert && (
                  <ErrorAlert
                    onCloseAlert={onCloseAlert}
                    alertDescription={alertDescription}
                  />
                )}
                <Card shadow loading={loading}>
                  <NameAndEmailForm
                    isNew={isNew}
                    type={type !== 'unregistered' ? type : isType}
                    defaultValues={{
                      email,
                      password,
                      phone,
                      lastName,
                      firstName,
                      corporationName,
                      corporationNameKana,
                    }}
                  />
                </Card>
              </Layout.Section>

              <Divider space="large" />

              <Layout.Section size="small">
                <Title level={5}>お客様の詳細</Title>
              </Layout.Section>
              <Layout.Section size="large">
                <Card shadow loading={loading}>
                  {/* 住所情報 */}
                  <AddressAndDetailsForm
                    form={form}
                    defaultValues={{
                      address2,
                      city,
                      zip,
                      address1,
                      prefectureCode,
                    }}
                  />
                </Card>
              </Layout.Section>

              <Divider space="large" />

              <Layout.Section size="small">
                <Title level={5}>メモ</Title>
              </Layout.Section>
              <Layout.Section size="large">
                <Card shadow loading={loading}>
                  <NoteForm defaultValue={note} />
                </Card>
              </Layout.Section>

              {!isNew && (
                <>
                  <Layout.Section size="small">
                    <Title level={5}>予約可能フラグの設定</Title>
                    <p className="opacity-90">
                      ユーザーページからの予約を制限するためのフラグを設定できます。
                      <br />
                      予約不可の場合はユーザーページに予約ボタンが表示されなくなります。
                    </p>
                  </Layout.Section>
                  <Layout.Section size="large">
                    <Card shadow loading={loading}>
                      <Form.Item
                        name="bookableFlag"
                        initialValue={defaultValues?.bookableFlag}
                      >
                        <Select className="w-1/2" onChange={setIsBookableFlag}>
                          <Select.Option value={true}>予約可能</Select.Option>
                          <Select.Option value={false}>予約不可</Select.Option>
                        </Select>
                      </Form.Item>
                    </Card>
                  </Layout.Section>
                </>
              )}

              {type === 'corporate' ||
                (isType === 'corporate' && (
                  <>
                    <Divider space="large" />

                    <Layout.Section size="small">
                      <Title level={5}>減免適用情報</Title>
                    </Layout.Section>
                    <Layout.Section size="large">
                      <Card shadow loading={loading}>
                        <DiscountForm appliedDiscount={appliedDiscount} />
                      </Card>
                    </Layout.Section>

                    {!generalSettingLoading &&
                      generalSettingData?.generalSetting.domain ===
                        'saga-arena' && (
                        <>
                          <Divider space="large" />
                          <Layout.Section size="small">
                            <Title level={5}>
                              サブアリーナ予約可能フラグの設定
                            </Title>
                            <p className="opacity-90">
                              サブアリーナの予約可否を設定できます。
                              <br />
                              予約可能の場合はユーザー利用者ページからサブアリーナを予約することができるようになります。
                            </p>
                          </Layout.Section>
                          <Layout.Section size="large">
                            <Card shadow loading={loading}>
                              <Form.Item
                                name="subArenaBookableFlag"
                                initialValue={
                                  defaultValues?.subArenaBookableFlag ?? false
                                }
                              >
                                <Select
                                  className="w-1/2"
                                  onChange={setIsSubArenaFlag}
                                >
                                  <Select.Option value={true}>
                                    予約可能
                                  </Select.Option>
                                  <Select.Option value={false}>
                                    予約不可
                                  </Select.Option>
                                </Select>
                              </Form.Item>
                            </Card>
                          </Layout.Section>

                          <Divider space="large" />

                          <Layout.Section size="small">
                            <Title level={5}>県内団体フラグの設定</Title>
                            <p className="opacity-90">
                              県内または県外の団体かを設定できます。
                              <br />
                              県内団体の場合は通常とは異なる期間でSAGAアリーナ内の施設を予約することができるようになります。
                            </p>
                            <p className="opacity-90">
                              ※サブアリーナ予約可能フラグを予約可能にすることで設定できます。
                            </p>
                          </Layout.Section>
                          <Layout.Section size="large">
                            <Card shadow loading={loading}>
                              <Form.Item
                                name="prefecturalGroupFlag"
                                initialValue={
                                  defaultValues?.prefecturalGroupFlag ?? false
                                }
                              >
                                <Select
                                  className="w-1/2"
                                  disabled={!isSubArenaFlag}
                                >
                                  <Select.Option value={true}>
                                    県内団体
                                  </Select.Option>
                                  <Select.Option value={false}>
                                    県外団体
                                  </Select.Option>
                                </Select>
                              </Form.Item>
                            </Card>
                          </Layout.Section>
                        </>
                      )}
                  </>
                ))}

              <Layout.Footer
                left={
                  <Button
                    danger
                    htmlType="button"
                    onClick={onCancel}
                    disabled={requesting}
                  >
                    キャンセル
                  </Button>
                }
                right={
                  <div className={clsx('flex', 'justify-end')}>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={requesting}
                    >
                      保存する
                    </Button>
                  </div>
                }
              />
            </>
          )}
        </Layout>
      </Form>
    </>
  )
}
